import React, { useState } from 'react';
import { RefreshCw, Save, AlertTriangle, CheckCircle } from 'lucide-react';
import ProblemDescriptionEditor from './ProblemDescriptionEditor';
import PrincipleAnswersEditor from './PrincipleAnswersEditor';
import { UserInputTabProps } from '../../types/components';
import { UserAnswer } from '../../types/problemDiagnosis';

const UserInputTab: React.FC<UserInputTabProps> = ({
  session,
  answers,
  editState,
  onEditToggle,
  onSave,
  onRegenerateReport,
  onDraftSave,
  isRegenerating
}) => {
  const [localProblemDescription, setLocalProblemDescription] = useState(session.problem_description);
  const [localAnswers, setLocalAnswers] = useState<UserAnswer[]>(answers);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleProblemDescriptionChange = (value: string) => {
    setLocalProblemDescription(value);
    setHasUnsavedChanges(true);
  };

  const handleAnswersChange = (newAnswers: UserAnswer[]) => {
    setLocalAnswers(newAnswers);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    try {
      await onSave({
        problem_description: localProblemDescription,
        answers: localAnswers
      });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('保存失败:', error);
    }
  };

  const handleCancel = () => {
    setLocalProblemDescription(session.problem_description);
    setLocalAnswers(answers);
    setHasUnsavedChanges(false);
    onEditToggle(false);
  };

  const handleProblemDescriptionAutoSave = (value: string) => {
    onDraftSave('problem_description', value);
  };

  const handleAnswersAutoSave = (newAnswers: UserAnswer[]) => {
    onDraftSave('answers', newAnswers);
  };

  const canRegenerateReport = () => {
    return localProblemDescription.trim() && 
           localAnswers.some(answer => answer.user_answer.trim());
  };

  const getCompletionStats = () => {
    const completedAnswers = localAnswers.filter(answer => answer.user_answer.trim()).length;
    const totalAnswers = localAnswers.length;
    return { completed: completedAnswers, total: totalAnswers };
  };

  const stats = getCompletionStats();

  return (
    <div className="space-y-4">
      {/* 页面头部信息 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          {/* 左侧：完成度统计 */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                localProblemDescription.trim() ? 'bg-green-500' : 'bg-gray-300'
              }`} />
              <span className="text-sm text-gray-700">问题描述</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                stats.completed === stats.total ? 'bg-green-500' : 
                stats.completed > 0 ? 'bg-yellow-500' : 'bg-gray-300'
              }`} />
              <span className="text-sm text-gray-700">
                五大原理分析 ({stats.completed}/{stats.total})
              </span>
            </div>

            {/* 整体完成度提示 */}
            {stats.completed === stats.total && localProblemDescription.trim() && (
              <div className="flex items-center space-x-1 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">内容完整</span>
              </div>
            )}
          </div>

          {/* 右侧：操作按钮 */}
          <div className="flex items-center space-x-3">
            {editState.isEditing && (
              <>
                <button
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  取消
                </button>
                
                <button
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                >
                  <Save className="w-3 h-3" />
                  <span>保存</span>
                </button>
              </>
            )}
            
            {!editState.isEditing && canRegenerateReport() && (
              <button
                onClick={onRegenerateReport}
                disabled={isRegenerating}
                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
              >
                {isRegenerating ? (
                  <>
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    <span>生成中</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3 h-3" />
                    <span>重新生成报告</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* 未保存更改提示 */}
        {hasUnsavedChanges && (
          <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded-md">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-3 h-3 text-orange-600" />
              <span className="text-xs text-orange-800">
                有未保存的更改
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 问题描述编辑器 */}
      <ProblemDescriptionEditor
        value={localProblemDescription}
        onChange={handleProblemDescriptionChange}
        isEditing={editState.isEditing && editState.editingField === 'problem_description'}
        onEditToggle={() => onEditToggle(true, 'problem_description')}
        onSave={handleSave}
        onCancel={handleCancel}
        onAutoSave={handleProblemDescriptionAutoSave}
      />

      {/* 五大原理回答编辑器 */}
      <PrincipleAnswersEditor
        answers={localAnswers}
        onChange={handleAnswersChange}
        isEditing={editState.isEditing && editState.editingField === 'answers'}
        onEditToggle={() => onEditToggle(true, 'answers')}
        onSave={handleSave}
        onCancel={handleCancel}
        onAutoSave={handleAnswersAutoSave}
      />


    </div>
  );
};

export default UserInputTab;